// Globals
import { useTranslation } from 'react-i18next';
import { useWatch } from 'antd/es/form/Form';

// Models
import { AuthorRole, CommentType, IOrderComment, OrdersStatus } from '@/models/orders.model';

// Components
import { TextArea } from '@/ui';
import {
  StyledWrapper,
  StyledTitle,
  StyledCommentWrapper,
  StyledCommentHeader,
  StyledCommentText,
  StyledDate,
  StyledUserName,
  StyledUserNameWrapper,
  StyledUserRole,
  StyledAddCommentBlock,
  StyledQuickCommentsBlock,
  StyledQuickCommentsLabel,
  StyledQuickCommentsSelect,
  StyledButton,
} from './CommentsStyles';
import { FormItem, IFormInstance } from '@/components';

// Helpers
import { formatDate } from '@/utils';
import { DateFormats } from '@/constants/dates';
import {
  commentEditStatuses,
  OrderDetailsFormFieldsNames,
  QUICK_COMMENT_TEMPLATE_KEYS,
  useUpdateProductsAndCommentsFormRules,
} from '../../helpers';

interface CommentsProps {
  comments?: IOrderComment[];
  subOrderId?: string;
  handleSubmit: (subOrderId?: string) => void;
  isLoading: boolean;
  formInstance: IFormInstance;
  orderStatus: OrdersStatus;
  canManage?: boolean;
}

const Comments = ({
  comments,
  handleSubmit,
  isLoading,
  subOrderId,
  formInstance,
  orderStatus,
  canManage,
}: CommentsProps) => {
  const { t } = useTranslation(['orders']);
  const validationRules = useUpdateProductsAndCommentsFormRules(t);
  const commentRules = validationRules[OrderDetailsFormFieldsNames.COMMENTS].default;
  const watchedComment = useWatch([OrderDetailsFormFieldsNames.COMMENTS, subOrderId], formInstance);
  const isCommentEnabled = commentEditStatuses?.has(orderStatus);

  const quickCommentOptions = QUICK_COMMENT_TEMPLATE_KEYS.map((key) => {
    const label = t(key);
    return { value: label, label };
  });

  const handleQuickCommentSelect = (value: unknown) => {
    if (typeof value !== 'string' || !subOrderId) {
      return;
    }

    formInstance.setFieldValue([OrderDetailsFormFieldsNames.COMMENTS, subOrderId], value);
  };

  return (
    <StyledWrapper>
      <StyledTitle>{`${t('order_details_comments')} (${comments?.length || 0})`}</StyledTitle>
      {comments?.map((comment) => (
        <StyledCommentWrapper key={comment.id}>
          <StyledCommentHeader>
            <StyledUserNameWrapper>
              <StyledUserName>{comment.author_name}</StyledUserName>
              <StyledUserRole
                $isAdmin={comment.author_role === AuthorRole.ADMIN || comment.author_role === AuthorRole.SUPER_ADMIN}
              >
                {comment.author_role === AuthorRole.PARTNER || comment.author_role === AuthorRole.PARTNER_MANAGER
                  ? t('order_details_comments_partner')
                  : t('order_details_comments_admin')}
              </StyledUserRole>
            </StyledUserNameWrapper>
            <StyledDate>{formatDate(comment.created_at, DateFormats.EUROPEAN)}</StyledDate>
          </StyledCommentHeader>
          <StyledCommentText $isCancellation={comment.type === CommentType.CANCELLATION}>
            {comment.content}
          </StyledCommentText>
        </StyledCommentWrapper>
      ))}
      {canManage && (
        <StyledAddCommentBlock>
          <StyledQuickCommentsBlock>
            <StyledQuickCommentsLabel>{t('order_details_quick_comments_label')}</StyledQuickCommentsLabel>
            <StyledQuickCommentsSelect
              placeholder={t('order_details_quick_comments_placeholder')}
              options={quickCommentOptions}
              value={null}
              onChange={handleQuickCommentSelect}
              disabled={!isCommentEnabled}
            />
          </StyledQuickCommentsBlock>
          <FormItem
            name={[OrderDetailsFormFieldsNames.COMMENTS, subOrderId]}
            label={t('order_details_add_comment_label')}
            rules={commentRules}
          >
            <TextArea rows={4} />
          </FormItem>
          <StyledButton
            onClick={() => handleSubmit(subOrderId)}
            type="primary"
            loading={isLoading}
            disabled={!watchedComment || !isCommentEnabled}
          >
            {t('order_details_comments_send_button')}
          </StyledButton>
        </StyledAddCommentBlock>
      )}
    </StyledWrapper>
  );
};

export default Comments;
